
import { useState } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card } from "@/components/ui/card";
import { 
  PenLine, 
  Image, 
  Tags,
  CalendarCheck,
  Wand2
} from "lucide-react";
import { BlogPostFormData } from "@/types/blog";
import { ContentTab } from "./form/ContentTab";
import { MetadataTab } from "./form/MetadataTab";
import { SEOTab } from "./form/SEOTab";
import { PublishTab } from "./form/PublishTab";
import { GenerateContent } from "./GenerateContent";

interface BlogPostFormProps {
  formData: BlogPostFormData;
  onChange: (field: string, value: any) => void;
  isLoading?: boolean;
}

const BlogPostForm = ({ formData, onChange, isLoading = false }: BlogPostFormProps) => {
  const [activeTab, setActiveTab] = useState("generate");

  // Handle AI generated content
  const handleContentGenerated = (generatedContent: any) => {
    // Update title
    if (generatedContent.title) {
      onChange("title", generatedContent.title);
    }
    
    // Update content
    if (generatedContent.content) {
      onChange("content", generatedContent.content);
    }
    
    // Update excerpt
    if (generatedContent.excerpt) {
      onChange("excerpt", generatedContent.excerpt);
    }
    
    // Update slug if provided
    if (generatedContent.slug) {
      onChange("slug", generatedContent.slug);
    }
    
    // Ensure metadata exists
    const currentMetadata = formData.metadata || {
      author: "",
      categories: [],
      tags: [],
      featured: false,
      seo_description: "",
      sources: []
    };
    
    // Handle metadata updates
    if (generatedContent.metadata || generatedContent.author || generatedContent.tags || generatedContent.categories || generatedContent.sources) {
      const newMetadata = { ...currentMetadata };
      
      // Update author
      if (generatedContent.metadata?.author || generatedContent.author) {
        newMetadata.author = generatedContent.metadata?.author || generatedContent.author;
      }
      
      // Update tags
      if (generatedContent.metadata?.tags || generatedContent.tags) {
        newMetadata.tags = generatedContent.metadata?.tags || generatedContent.tags || [];
      }
      
      // Update categories
      if (generatedContent.metadata?.categories || generatedContent.categories) {
        newMetadata.categories = generatedContent.metadata?.categories || generatedContent.categories || [];
      }
      
      // Update sources
      if (generatedContent.metadata?.sources || generatedContent.sources) {
        const sourcesArray = generatedContent.metadata?.sources || generatedContent.sources || [];
        newMetadata.sources = sourcesArray.map((source: any) => ({
          description: source.description || source.publication_name || "",
          url: source.url || ""
        }));
      }
      
      // Update SEO description
      if (generatedContent.seo_description || generatedContent.metadata?.seo_description) {
        newMetadata.seo_description = generatedContent.seo_description || generatedContent.metadata?.seo_description || "";
      }
      
      onChange("metadata", newMetadata);
    }
    
    // Move to the Content tab after generating
    setActiveTab("content");
  };

  // Check if form has data
  const formHasData = Boolean(
    formData.title.trim() || 
    formData.content.trim() || 
    formData.excerpt.trim() ||
    formData.slug.trim() ||
    (formData.metadata?.tags && formData.metadata.tags.length > 0) ||
    (formData.metadata?.categories && formData.metadata.categories.length > 0) ||
    (formData.metadata?.sources && formData.metadata.sources.length > 0) ||
    formData.metadata?.seo_description?.trim()
  );

  return (
    <div className="space-y-6">
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid grid-cols-5 w-full">
          <TabsTrigger value="generate" className="flex items-center gap-2">
            <Wand2 className="h-4 w-4" />
            Generate
          </TabsTrigger>
          <TabsTrigger value="content" className="flex items-center gap-2">
            <PenLine className="h-4 w-4" />
            Content
          </TabsTrigger>
          <TabsTrigger value="metadata" className="flex items-center gap-2">
            <Image className="h-4 w-4" />
            Metadata
          </TabsTrigger>
          <TabsTrigger value="seo" className="flex items-center gap-2">
            <Tags className="h-4 w-4" />
            SEO
          </TabsTrigger>
          <TabsTrigger value="publish" className="flex items-center gap-2">
            <CalendarCheck className="h-4 w-4" />
            Publish
          </TabsTrigger>
        </TabsList>
        
        <TabsContent value="generate" className="space-y-4 mt-6">
          <GenerateContent 
            onContentGenerated={handleContentGenerated} 
            formHasData={formHasData}
            isLoading={isLoading}
          />
        </TabsContent>
        
        <TabsContent value="content" className="mt-6">
          <ContentTab 
            formData={formData} 
            onChange={onChange} 
            isLoading={isLoading} 
          />
        </TabsContent>
        
        <TabsContent value="metadata" className="space-y-4 mt-4">
          <Card className="p-6">
            <MetadataTab 
              formData={formData} 
              onChange={onChange} 
              isLoading={isLoading} 
            />
          </Card>
        </TabsContent>
        
        <TabsContent value="seo" className="space-y-4 mt-4">
          <Card className="p-6">
            <SEOTab 
              formData={formData} 
              onChange={onChange} 
              isLoading={isLoading} 
            />
          </Card>
        </TabsContent>
        
        <TabsContent value="publish" className="space-y-4 mt-6">
          <PublishTab 
            formData={formData} 
            onChange={onChange} 
            isLoading={isLoading} 
          />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default BlogPostForm;
